import type { Locale } from "../i18n/translations.js";

export interface LearnLifeContext {
  readonly title: string;
  readonly text: string;
}

type LocalizedLifeContexts = Record<Locale, readonly LearnLifeContext[]>;

/**
 * Short, practical contexts that sit beside the source-bound core guidance.
 * They are deliberately scenario prompts, not replacement emergency instructions.
 */
export const learnLifeContexts: Readonly<Record<string, LocalizedLifeContexts>> = {
  earthquake: {
    th: [
      { title: "โรงเรียน", text: "ให้เด็กฝึกหมอบ–กำบัง–ยึดกับครู และรอคำสั่งรวมแถวหลังหยุดสั่น" },
      {
        title: "ผู้สูงอายุ",
        text: "วางแว่น ยา รองเท้า และอุปกรณ์ช่วยเดินใกล้จุดที่ปลอดภัย โดยไม่กีดขวางทางออก",
      },
    ],
    en: [
      {
        title: "School",
        text: "Help children practise Drop, Cover, and Hold On with staff, then wait for the assembly instruction.",
      },
      {
        title: "Older adult",
        text: "Keep glasses, medicines, shoes, and mobility aids near a safe point without blocking an exit.",
      },
    ],
  },
  tsunami: {
    th: [
      {
        title: "เที่ยวชายฝั่ง",
        text: "ก่อนลงหาด รู้เส้นทางขึ้นที่สูงและจุดนัดพบของกลุ่ม ไม่พึ่งโทรศัพท์อย่างเดียว",
      },
      {
        title: "เด็กและผู้ดูแล",
        text: "พาเด็กออกจากชายฝั่งทันทีเมื่อมีสัญญาณธรรมชาติหรือคำเตือน ไม่ย้อนกลับไปหาเล่นหรือถ่ายภาพ",
      },
    ],
    en: [
      {
        title: "Coastal trip",
        text: "Before going to the beach, know the uphill route and group meeting point; do not rely on a phone alone.",
      },
      {
        title: "Children and carers",
        text: "Move children away from the coast immediately after a natural sign or warning; do not return for toys or photos.",
      },
    ],
  },
  "flood-storm": {
    th: [
      {
        title: "ไปโรงเรียนหรือทำงาน",
        text: "เช็กเส้นทางและประกาศก่อนออกบ้าน หากปิดทางหรือมีน้ำ ให้แจ้งปลายทางและเลือกไม่เดินทาง",
      },
      {
        title: "ร้านค้าและของส่ง",
        text: "อย่ารับของหรือเปิดประตูในน้ำขังใกล้ระบบไฟฟ้า รอให้พื้นที่ปลอดภัยก่อน",
      },
    ],
    en: [
      {
        title: "School or work commute",
        text: "Check routes and notices before leaving. If water or a closure blocks the way, notify the destination and do not travel.",
      },
      {
        title: "Deliveries and shops",
        text: "Do not collect deliveries or open access points through standing water near electricity; wait until the area is safe.",
      },
    ],
  },
  "fire-evacuation": {
    th: [
      {
        title: "เด็กในบ้าน",
        text: "สอนให้รู้เสียงสัญญาณ ทางออก และจุดนัดพบ แต่ไม่ให้กลับเข้าไปหาใครหรือของ",
      },
      {
        title: "คนเคลื่อนย้ายลำบาก",
        text: "แจ้งผู้ดูแลอาคารหรือคนใกล้ตัวล่วงหน้าว่าต้องการความช่วยเหลือ และรู้จุดรอที่สถานที่กำหนด",
      },
    ],
    en: [
      {
        title: "Children at home",
        text: "Teach the alarm sound, exit route, and meeting point, but not to go back inside for a person or belongings.",
      },
      {
        title: "Limited mobility",
        text: "Tell building staff or trusted people about assistance needs ahead of time and know the designated waiting point.",
      },
    ],
  },
  chemicals: {
    th: [
      {
        title: "เด็กและสัตว์เลี้ยง",
        text: "เก็บสารในภาชนะเดิมให้พ้นมือและไม่เรียกผลิตภัณฑ์ว่าเป็นน้ำหรือขนม",
      },
      {
        title: "ซักผ้าหรือทำความสะอาด",
        text: "ใช้ทีละผลิตภัณฑ์ตามฉลาก ล้างมือหลังใช้ และอย่าเทสารลงภาชนะอาหาร",
      },
    ],
    en: [
      {
        title: "Children and pets",
        text: "Keep products in their original containers out of reach; never describe them as drinks or treats.",
      },
      {
        title: "Laundry or cleaning",
        text: "Use one product at a time as labelled, wash hands afterward, and never put chemicals in food containers.",
      },
    ],
  },
  "first-aid": {
    th: [
      {
        title: "กีฬาและสนามเด็กเล่น",
        text: "หยุดกิจกรรม ดูอันตรายรอบตัว และให้ผู้ใหญ่ที่รับผิดชอบช่วยประเมินก่อนพาเด็กกลับเล่น",
      },
      {
        title: "อยู่คนเดียว",
        text: "หากไม่ปลอดภัยหรืออาการรุนแรง ให้โทรหรือส่งข้อความขอความช่วยเหลือแทนการขับรถไปเอง",
      },
    ],
    en: [
      {
        title: "Sport or playground",
        text: "Stop the activity, check nearby hazards, and have a responsible adult assess before a child returns to play.",
      },
      {
        title: "Alone",
        text: "If you are unsafe or symptoms are severe, call or message for help instead of driving yourself.",
      },
    ],
  },
  "public-safety": {
    th: [
      {
        title: "รถไฟฟ้าหรือรถโดยสาร",
        text: "เมื่อเห็นความวุ่นวาย อย่าฝืนไปดูหรือสวนฝูงชน เลือกออกตามทางที่ปลอดภัยและฟังเจ้าหน้าที่",
      },
      {
        title: "มากับเด็ก",
        text: "ตกลงจุดนัดพบง่าย ๆ ก่อนเข้าไป และสอนให้เด็กหาพนักงานในเครื่องแบบหากพลัดหลง",
      },
    ],
    en: [
      {
        title: "Rail or bus",
        text: "When trouble starts, do not push toward it or against a crowd. Leave by a safer route and listen to staff.",
      },
      {
        title: "With a child",
        text: "Agree on a simple meeting point before entering, and teach the child to seek a uniformed staff member if separated.",
      },
    ],
  },
  "family-preparedness": {
    th: [
      {
        title: "ไฟดับหรือเน็ตล่ม",
        text: "มีรายชื่อและจุดนัดพบแบบกระดาษ ตกลงเวลาที่จะลองติดต่อใหม่ และประหยัดแบตเตอรี่",
      },
      {
        title: "อยู่คนละที่",
        text: "ให้ทุกคนส่งข้อความสั้นว่าอยู่ไหนและปลอดภัยเมื่อทำได้ แทนการโทรซ้ำจนเครือข่ายแน่น",
      },
    ],
    en: [
      {
        title: "Power or network outage",
        text: "Keep contacts and meeting points on paper, agree when to try again, and save battery power.",
      },
      {
        title: "Separated household",
        text: "Have everyone send a short location-and-safe message when possible instead of repeatedly calling into a busy network.",
      },
    ],
  },
  medicines: {
    th: [
      {
        title: "งานเป็นกะหรือเดินทาง",
        text: "พกยาในภาชนะติดฉลากและตั้งเตือนเวลาใช้ยา อย่าปรับเวลา ปริมาณ หรือใช้ยาชดเชยเอง",
      },
      {
        title: "ยาของเด็ก",
        text: "ใช้ฉลากและอุปกรณ์ตวงที่มากับยา ถ้าไม่แน่ใจเรื่องขนาดหรือวิธีใช้ให้ถามเภสัชกรหรือผู้สั่งยา",
      },
    ],
    en: [
      {
        title: "Shift work or travel",
        text: "Carry medicine in its labelled container and set a reminder; do not change timing, dose, or make up a dose on your own.",
      },
      {
        title: "A child’s medicine",
        text: "Use the label and the supplied measuring device. Ask a pharmacist or prescriber when dose or use is unclear.",
      },
    ],
  },
  "food-labels": {
    th: [
      {
        title: "อาหารที่โรงเรียน",
        text: "ให้เด็กและผู้ดูแลรู้ชื่ออาหารที่ต้องหลีกเลี่ยง และไม่แลกขนมหรือชิมของเพื่อนเมื่อข้อมูลไม่ชัด",
      },
      {
        title: "บุฟเฟต์หรือของแบ่งกัน",
        text: "ตักด้วยอุปกรณ์ของตนเองและถามก่อนกิน เพราะช้อนกลางหรือป้ายอาหารอาจไม่บอกการปนเปื้อนครบ",
      },
    ],
    en: [
      {
        title: "School food",
        text: "Make sure the child and carers know foods to avoid; do not swap snacks or taste a friend’s food when information is unclear.",
      },
      {
        title: "Buffet or shared food",
        text: "Use your own serving utensil and ask before eating: shared spoons and labels may not show every cross-contact risk.",
      },
    ],
  },
};
